// Two-level drill-down modal used by the Financial Report cells.
//
// Level 1: list of players matching the (date range, site, payment_method_type)
//          scope, sorted by total deposits desc. Shows per-player aggregates.
// Level 2: individual finance events (deposits + withdrawals) for a selected
//          player in the same scope — populated on demand.
//
// Scope is passed in by the calling panel so one generic modal can be triggered
// from Overview, Mix, Deposits, Withdrawals or FTD cells.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronRight, ArrowLeft } from 'lucide-react';
import { getPlayerCashier, getCashierTransactions } from '../../api/client';
import { formatAmount } from '../../lib/format';
import { currencySymbol, type CurrencyMode } from '../CurrencySelector';
import { paymentMethodTypeBadgeClasses, paymentMethodTypeLabel, type PaymentMethodType } from '../PaymentMethodTypeFilter';
import { siteName, useSites } from '../SiteFilter';

export interface DrillDownScope {
  dateFrom: string;
  dateTo: string;
  siteIds: number[];        // narrow — typically a single site from the clicked row
  currencyMode: CurrencyMode;
  pmType?: PaymentMethodType;
  // Used to label the modal header.
  title: string;
  // Restrict event types for transactions view. Default = deposits + withdrawals.
  eventTypes?: string[];
  // When true, only show first-time depositors (FTD drill-down).
  ftdOnly?: boolean;
}

interface DrillDownModalProps {
  open: boolean;
  onClose: () => void;
  scope: DrillDownScope | null;
}

interface PlayerRow {
  player_id: string;
  site_id: number;
  total_deposits: number;
  total_withdrawals: number;
  net_cash: number;
  deposit_count: number;
  withdrawal_count: number;
  first_deposit_at?: string;
  last_activity_at?: string;
}

interface TxRow {
  event_date: string;
  event_type: string;
  player_id: string;
  site_id: number;
  payment_method?: string;
  payment_method_type?: string;
  amount: number;
  amount_eur: number;
  currency: string;
  movement_id?: number;
  external_transaction_id?: string;
  is_first_deposit?: boolean;
}

export const DrillDownModal: React.FC<DrillDownModalProps> = ({ open, onClose, scope }) => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<TxRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const symbol = scope ? currencySymbol(scope.currencyMode) : '';

  // Fetch players when the modal opens (level 1).
  useEffect(() => {
    if (!open || !scope) return;
    setSelectedPlayer(null);
    setTransactions([]);
    setLoading(true);
    setError(null);
    getPlayerCashier({
      date_from: scope.dateFrom,
      date_to: scope.dateTo,
      site_ids: scope.siteIds.join(','),
      display_currency: scope.currencyMode,
      ftd_only: scope.ftdOnly || undefined,
      limit: 50,
    })
      .then((res) => setPlayers(res.data || []))
      .catch((err) => setError(String(err?.message || err)))
      .finally(() => setLoading(false));
  }, [open, scope]);

  // Fetch transactions when a player is selected (level 2).
  useEffect(() => {
    if (!selectedPlayer || !scope) return;
    setLoading(true);
    setError(null);
    getCashierTransactions({
      date_from: scope.dateFrom,
      date_to: scope.dateTo,
      site_ids: scope.siteIds.join(','),
      player_id: selectedPlayer,
      payment_method_type: scope.pmType,
      event_types: (scope.eventTypes || ['deposit_completed', 'withdrawal_paid']).join(','),
      limit: 200,
    })
      .then((res) => setTransactions(res.data || []))
      .catch((err) => setError(String(err?.message || err)))
      .finally(() => setLoading(false));
  }, [selectedPlayer, scope]);

  // Close on ESC for accessibility.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !scope) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-lg border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-slate-700">
          <div className="flex items-center gap-3">
            {selectedPlayer && (
              <button
                onClick={() => setSelectedPlayer(null)}
                className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
                title={t('drillDown.back', { defaultValue: 'Back to players' })}
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {selectedPlayer
                  ? t('drillDown.transactionsTitle', { defaultValue: 'Transactions — Player {{id}}', id: selectedPlayer })
                  : scope.title}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {scope.dateFrom === scope.dateTo ? scope.dateFrom : `${scope.dateFrom} → ${scope.dateTo}`}
                {' · '}
                {scope.siteIds.length === 1 ? siteName(allSites, scope.siteIds[0]) : `${scope.siteIds.length} sites`}
                {scope.pmType && <> {' · '}<span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${paymentMethodTypeBadgeClasses(scope.pmType)}`}>{paymentMethodTypeLabel(scope.pmType)}</span></>}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
            title={t('drillDown.close', { defaultValue: 'Close' })}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && <div className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-300">{error}</div>}
          {loading && <div className="py-8 text-center text-sm text-slate-500">{t('filters.loading')}</div>}

          {!loading && !selectedPlayer && (
            <PlayersTable rows={players} symbol={symbol} onSelect={setSelectedPlayer} />
          )}
          {!loading && selectedPlayer && (
            <TransactionsTable rows={transactions} symbol={symbol} />
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Level 1: players table ----------

const PlayersTable: React.FC<{ rows: PlayerRow[]; symbol: string; onSelect: (id: string) => void }> = ({ rows, symbol, onSelect }) => {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return <div className="py-8 text-center text-sm text-slate-500">{t('financialReport.noPlayerResults', { defaultValue: 'No players match the filters.' })}</div>;
  }
  const fmt = (v: number) => `${symbol}${formatAmount(v || 0)}`;
  return (
    <table className="w-full text-left text-sm">
      <thead className="bg-slate-50 dark:bg-slate-800">
        <tr>
          {[
            t('financialReport.columnPlayerId', { defaultValue: 'Player ID' }),
            t('financialReport.kpiTotalDeposits', { defaultValue: 'Deposits' }),
            t('financialReport.kpiTotalWithdrawals', { defaultValue: 'Withdrawals' }),
            t('financialReport.columnNetCash', { defaultValue: 'Net' }),
            '# D/W',
            '',
          ].map((h, i) => (
            <th key={i} className="px-4 py-2 font-medium text-slate-700 dark:text-slate-300">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
        {rows.map((r) => (
          <tr
            key={r.player_id}
            onClick={() => onSelect(r.player_id)}
            className="cursor-pointer bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800"
          >
            <td className="px-4 py-2 font-mono text-xs text-slate-900 dark:text-white">{r.player_id}</td>
            <td className="px-4 py-2 text-emerald-700 dark:text-emerald-400">{fmt(r.total_deposits)}</td>
            <td className="px-4 py-2 text-red-700 dark:text-red-400">{fmt(r.total_withdrawals)}</td>
            <td className={`px-4 py-2 font-semibold ${r.net_cash >= 0 ? 'text-blue-700 dark:text-blue-400' : 'text-red-700 dark:text-red-400'}`}>{fmt(r.net_cash)}</td>
            <td className="px-4 py-2 text-slate-900 dark:text-white">{r.deposit_count || 0} / {r.withdrawal_count || 0}</td>
            <td className="px-4 py-2 text-slate-400"><ChevronRight className="h-4 w-4" /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ---------- Level 2: transactions table ----------

const TransactionsTable: React.FC<{ rows: TxRow[]; symbol: string }> = ({ rows, symbol }) => {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return <div className="py-8 text-center text-sm text-slate-500">{t('financialReport.noDataAvailable')}</div>;
  }
  return (
    <table className="w-full text-left text-sm">
      <thead className="bg-slate-50 dark:bg-slate-800">
        <tr>
          {[
            t('drillDown.columnTime', { defaultValue: 'When' }),
            t('drillDown.columnEventType', { defaultValue: 'Event' }),
            t('financialReport.columnPaymentMethod', { defaultValue: 'Method' }),
            t('financialReport.columnPaymentType', { defaultValue: 'Type' }),
            t('drillDown.columnAmountNative', { defaultValue: 'Amount (native)' }),
            t('drillDown.columnAmountEur', { defaultValue: 'Amount (EUR)' }),
            t('drillDown.columnExternalId', { defaultValue: 'External ID' }),
          ].map((h) => (
            <th key={h} className="px-4 py-2 font-medium text-slate-700 dark:text-slate-300">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
        {rows.map((r, i) => {
          const isDeposit = r.event_type === 'deposit_completed';
          return (
            <tr key={i} className="bg-white dark:bg-slate-900">
              <td className="px-4 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">{r.event_date}</td>
              <td className="px-4 py-2">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${isDeposit ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'}`}>
                  {r.event_type}
                  {r.is_first_deposit && <span className="ml-1 rounded bg-amber-200 px-1 text-[10px] font-bold text-amber-900" title="First Time Deposit">FTD</span>}
                </span>
              </td>
              <td className="px-4 py-2 text-slate-900 dark:text-white">{r.payment_method || ''}</td>
              <td className="px-4 py-2">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${paymentMethodTypeBadgeClasses(r.payment_method_type)}`}>
                  {paymentMethodTypeLabel(r.payment_method_type)}
                </span>
              </td>
              <td className="px-4 py-2 text-slate-900 dark:text-white">
                {formatAmount(r.amount || 0)} {r.currency}
              </td>
              <td className={`px-4 py-2 font-semibold ${isDeposit ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                {symbol || '€'}{formatAmount(r.amount_eur || 0)}
              </td>
              <td className="px-4 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">{r.external_transaction_id || ''}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
